import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { io } from "socket.io-client";
import { useAuth } from "./AuthContext";
import { API_BASE_URL } from "@/lib/apiBase";
import { getAccessToken } from "@/lib/authSession";

const NotificationsSocketContext = createContext(null);

/**
 * User-scoped Socket.IO for notification bell + invite badges.
 * Stays connected whenever the user is authenticated (any workspace).
 */
export function NotificationsSocketProvider({ children }) {
  const { isAuthenticated } = useAuth();
  const socketRef = useRef(null);
  const listenersRef = useRef(new Set());
  const [connected, setConnected] = useState(false);

  const subscribe = useCallback((handler) => {
    listenersRef.current.add(handler);
    return () => listenersRef.current.delete(handler);
  }, []);

  const emitLocal = useCallback((event, payload) => {
    for (const handler of listenersRef.current) {
      try {
        handler(event, payload);
      } catch (err) {
        console.error("Notifications socket listener error:", err);
      }
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      setConnected(false);
      return undefined;
    }

    const socket = io(`${API_BASE_URL}/notifications`, {
      auth: (cb) => cb({ token: getAccessToken() }),
      transports: ["websocket", "polling"],
      withCredentials: true,
    });
    socketRef.current = socket;

    const onConnect = () => setConnected(true);
    const onDisconnect = () => setConnected(false);
    const onCreated = (payload) => emitLocal("notification.created", payload);
    const onUnread = (payload) =>
      emitLocal("notification.unread_count", payload);
    const onInvites = (payload) => emitLocal("invites.pending_count", payload);
    const onPartnership = (payload) =>
      emitLocal("partnership.updated", payload);

    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);
    socket.on("notification.created", onCreated);
    socket.on("notification.unread_count", onUnread);
    socket.on("invites.pending_count", onInvites);
    socket.on("partnership.updated", onPartnership);

    return () => {
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
      socket.off("notification.created", onCreated);
      socket.off("notification.unread_count", onUnread);
      socket.off("invites.pending_count", onInvites);
      socket.off("partnership.updated", onPartnership);
      socket.disconnect();
      if (socketRef.current === socket) socketRef.current = null;
      setConnected(false);
    };
  }, [isAuthenticated, emitLocal]);

  const value = {
    connected,
    subscribe,
  };

  return (
    <NotificationsSocketContext.Provider value={value}>
      {children}
    </NotificationsSocketContext.Provider>
  );
}

export function useNotificationsSocket() {
  const ctx = useContext(NotificationsSocketContext);
  if (!ctx) {
    throw new Error(
      "useNotificationsSocket must be used within NotificationsSocketProvider"
    );
  }
  return ctx;
}

export function useOptionalNotificationsSocket() {
  return useContext(NotificationsSocketContext);
}
